import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, X } from 'lucide-react';

const UsersAdmin = () => {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [newUserForm, setNewUserForm] = React.useState({ email: '', password: '', confirmPassword: '', role: 'admin' });
  const [formError, setFormError] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(null);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async () => {
    setFormError('');
    
    if (!newUserForm.email || !newUserForm.password) {
      setFormError('Email and password are required');
      return;
    }
    if (newUserForm.password !== newUserForm.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (newUserForm.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newUserForm.email,
          password: newUserForm.password,
          role: newUserForm.role
        })
      });

      if (response.ok) {
        setShowAddDialog(false);
        setNewUserForm({ email: '', password: '', confirmPassword: '', role: 'admin' });
        fetchUsers();
      } else {
        const data = await response.json();
        setFormError(data.detail || 'Failed to create user');
      }
    } catch (error) {
      setFormError('Network error. Please try again.');
    }
  };

  const handleToggleUserStatus = async (userId, currentActive) => {
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('token');
      
      if (currentActive) {
        // Suspend user (delete endpoint does soft delete)
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Reactivate user
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: true })
        });
      }
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE ${userEmail}? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(userId + '_delete');
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="text-center py-8">Loading users...</div>;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-gray-600 text-sm mt-1">Add, suspend, or reactivate admin users</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-pink-500 hover:bg-pink-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add Admin
        </Button>
      </div>

      <div className="space-y-4">
        {users.map((user) => (
          <div key={user.id} className="border rounded-lg p-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${user.role === 'super_admin' ? 'bg-purple-500' : 'bg-pink-500'}`}>
                {user.email.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-bold">{user.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                    {user.role === 'super_admin' ? '👑 Super Admin' : '🔧 Admin'}
                  </span>
                  {user.last_login && (
                    <span className="text-xs text-gray-500">
                      Last login: {new Date(user.last_login).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${user.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {user.active !== false ? '✓ Active' : '✗ Suspended'}
              </span>
              {user.role !== 'super_admin' && (
                <>
                  <Button
                    onClick={() => handleToggleUserStatus(user.id, user.active !== false)}
                    variant={user.active !== false ? 'destructive' : 'outline'}
                    size="sm"
                    disabled={actionLoading === user.id}
                    className="min-w-[100px]"
                  >
                    {actionLoading === user.id ? 'Loading...' : user.active !== false ? 'Suspend' : 'Reactivate'}
                  </Button>
                  <Button
                    onClick={() => handleDeleteUser(user.id, user.email)}
                    variant="outline"
                    size="sm"
                    disabled={actionLoading === user.id + '_delete'}
                    className="min-w-[80px] text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {actionLoading === user.id + '_delete' ? '...' : 'Delete'}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add User Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Add New Admin</h3>
              <button onClick={() => { setShowAddDialog(false); setFormError(''); }} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-2">Email *</label>
                <input
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Password *</label>
                <input
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Minimum 8 characters"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Confirm Password *</label>
                <input
                  type="password"
                  value={newUserForm.confirmPassword}
                  onChange={(e) => setNewUserForm({ ...newUserForm, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Re-enter password"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Role</label>
                <select
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                >
                  <option value="admin">Admin (Can manage menu & specials)</option>
                  <option value="super_admin">Super Admin (Full access)</option>
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <Button onClick={() => { setShowAddDialog(false); setFormError(''); }} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddUser} className="flex-1 bg-pink-500 hover:bg-pink-600 text-white">
                  Create Admin
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAdmin;
